<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <!-- Header -->
      <div class="page-header d-flex justify-content-between align-items-center">
        <h3 class="page-title">Bài viết Fanpage</h3>
        <div class="d-flex">
          <select class="form-control mr-2" v-model="selectedFanpageId" @change="fetchPosts">
            <option disabled value="">-- Chọn Fanpage --</option>
            <option v-for="fp in fanpages" :key="fp.id" :value="fp.id">{{ fp.name }}</option>
          </select>
          <button
            v-if="selectedFanpageId"
            class="btn btn-primary"
            data-toggle="modal"
            data-target="#post_modal"
            @click="openCreateModal"
          >
            <i class="fa fa-edit"></i> Tạo bài viết
          </button>
        </div>
      </div>

      <!-- Feed -->
      <div v-if="loading" class="text-center">Đang tải bài viết...</div>
      <div v-else>
        <div v-for="post in posts" :key="post.id" class="card shadow-sm mb-4 p-3">
          <!-- Header bài viết -->
          <div class="d-flex align-items-center mb-2">
            <div class="avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style="width:40px;height:40px;">
              <i class="fa fa-flag"></i>
            </div>
            <div class="ml-2">
              <strong>{{ currentFanpageName }}</strong><br />
              <small class="text-muted">{{ formatDate(post.created_time) }}</small>
            </div>
            <div class="ml-auto">
              <button class="btn btn-sm btn-outline-primary mr-2" @click="openEditModal(post)">
                <i class="fa fa-pencil"></i>
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(post.id)">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>

          <!-- Nội dung -->
          <div class="mb-2" v-if="post.message">{{ post.message }}</div>

          <!-- Ảnh/Video -->
          <div v-if="post.images && post.images.length" class="post-images">
            <div
              v-for="(img, i) in post.images"
              :key="i"
              class="mb-2"
            >
              <img :src="img" class="img-fluid rounded" />
            </div>
          </div>
          <div v-else-if="post.full_picture">
            <img :src="post.full_picture" class="img-fluid rounded" />
          </div>

          <!-- Link bài viết -->
          <div v-if="post.permalink_url" class="mt-2">
            <a :href="post.permalink_url" target="_blank" class="text-primary">Xem trên Facebook</a>
          </div>
        </div>

        <div v-if="posts.length === 0" class="text-center text-muted">
          Không có bài viết nào.
        </div>
      </div>

      <!-- Modal thêm/sửa -->
      <div id="post_modal" class="modal custom-modal fade" role="dialog">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ editingPostId ? 'Sửa bài viết' : 'Tạo bài viết mới' }}</h5>
              <button type="button" class="close" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="savePost">
                <div class="form-group">
                  <textarea class="form-control" v-model="postForm.message" placeholder="Bạn đang nghĩ gì?"></textarea>
                </div>
                <div class="form-group">
                  <input type="text" class="form-control" v-model="postForm.link" placeholder="Đính kèm link (tùy chọn)" />
                </div>
                <div class="form-group">
                  <input type="file" @change="handleFileUpload" />
                </div>
                <div class="submit-section text-right">
                  <button type="submit" class="btn btn-primary">{{ editingPostId ? 'Cập nhật' : 'Đăng' }}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal xác nhận xóa -->
      <div class="modal custom-modal fade" id="delete_post_modal" role="dialog">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-body text-center">
              <h4>Bạn có chắc muốn xóa bài viết này?</h4>
              <button class="btn btn-danger mr-2" @click="deletePost">Xóa</button>
              <button class="btn btn-secondary" data-dismiss="modal">Hủy</button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import api from '../api';
import { useToast } from 'vue-toastification';

const toast = useToast();

const fanpages = ref([]);
const posts = ref([]);
const selectedFanpageId = ref('');
const currentFanpageName = ref('');
const loading = ref(false);

const editingPostId = ref(null);
const postForm = reactive({ message: '', link: '', attachedMedia: [] });
const fileToUpload = ref(null);
const postToDeleteId = ref(null);

const fetchFanpages = async () => {
  try {
    const res = await api.get('/api/fanpages');
    fanpages.value = res.data;
  } catch {
    toast.error('Không thể tải danh sách fanpage.');
  }
};

const fetchPosts = async () => {
  if (!selectedFanpageId.value) return;
  loading.value = true;
  try {
    const res = await api.get(`/api/fanpages/${selectedFanpageId.value}/posts`);
    posts.value = res.data.data;
    const fp = fanpages.value.find(f => f.id === selectedFanpageId.value);
    currentFanpageName.value = fp ? fp.name : '';
  } catch {
    toast.error('Không thể tải bài viết.');
  } finally {
    loading.value = false;
  }
};

const handleFileUpload = (e) => {
  fileToUpload.value = e.target.files[0];
};

const uploadMediaToFacebook = async () => {
  if (!fileToUpload.value) return null;
  try {
    // 1. upload file lên CRM
    const formData = new FormData();
    formData.append('files', fileToUpload.value);
    const uploadRes = await api.post('/api/images/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const fileUrl = uploadRes.data[0].location;

    // 2. gửi sang Facebook
    const mediaRes = await api.post(`/api/fanpages/${selectedFanpageId.value}/media`, {
      url: fileUrl,
      video: fileToUpload.value.type.startsWith('video'),
    });
    const fbMedia = JSON.parse(mediaRes.data);
    return fbMedia.id;
  } catch {
    toast.error('Lỗi khi upload media.');
    return null;
  }
};

const savePost = async () => {
  try {
    let attachedMediaIds = [];
    const mediaId = await uploadMediaToFacebook();
    if (mediaId) attachedMediaIds.push(mediaId);

    if (editingPostId.value) {
      await api.put(`/api/fanpages/${selectedFanpageId.value}/posts/${editingPostId.value}`, {
        message: postForm.message,
      });
      toast.success('Cập nhật bài viết thành công!');
    } else {
      await api.post(`/api/fanpages/${selectedFanpageId.value}/posts`, {
        message: postForm.message,
        link: postForm.link,
        attachedMedia: attachedMediaIds,
      });
      toast.success('Đăng bài thành công!');
    }
    fetchPosts();
    resetForm();
  } catch {
    toast.error('Có lỗi xảy ra khi lưu bài viết.');
  }
};

const openCreateModal = () => {
  resetForm();
  $('#post_modal').modal('show');
};

const openEditModal = (post) => {
  editingPostId.value = post.id;
  postForm.message = post.message;
  postForm.link = '';
  postForm.attachedMedia = [];
  fileToUpload.value = null;
  $('#post_modal').modal('show');
};

const confirmDelete = (id) => {
  postToDeleteId.value = id;
  $('#delete_post_modal').modal('show');
};

const deletePost = async () => {
  try {
    await api.delete(`/api/fanpages/${selectedFanpageId.value}/posts/${postToDeleteId.value}`);
    toast.success('Xóa bài viết thành công!');
    fetchPosts();
  } catch {
    toast.error('Không thể xóa bài viết.');
  }
};

const resetForm = () => {
  editingPostId.value = null;
  postForm.message = '';
  postForm.link = '';
  postForm.attachedMedia = [];
  fileToUpload.value = null;
  $('#post_modal').modal('hide');
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleString();
};

onMounted(() => {
  fetchFanpages();
});
</script>

<style scoped>
.card {
  border-radius: 10px;
}
.post-images img {
  max-height: 300px;
  object-fit: cover;
}
</style>
